/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ApiEndpointMutation,
  BaseQueryExtraOptions,
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  MutationDefinition
} from '@reduxjs/toolkit/query/react';
import { prepareHeaders, responseHandlerNoAuthRedirect } from '../helpers';
import { Store, UnknownAction } from '@reduxjs/toolkit';
import { baseQueryWithAuth } from './baseQueries/baseQueryWithAuth';
import { baseQueryWithCircuitBreaker } from './baseQueries/baseQueryWithCircuitBreaker';
import { baseQueryWithRetry } from './baseQueries/baseQueryWithRetry';
import { baseQueryWithTimeout } from './baseQueries/BaseQueryWithTimeout';

export const CLIENT_API_PATH = '/api/client/';

const isTestEnv = import.meta.env.MODE === 'test';
const baseUrl = `${import.meta.env.VITE_CONTENT_DELIVERY_API || (isTestEnv ? 'http://localhost' : '')}${CLIENT_API_PATH}`;

export type baseQueryExtraOptions = BaseQueryExtraOptions<BaseQueryFn> & {
  ignoreDelayedAction?: boolean;
};

const CLIENT_API_TAG_TYPES = [
  'Sample',
  'CreditTrackerData',
  'CreditPlans',
  'CartData',
  'CheckoutData',
  'Search',
  'LibraryItem',
  'Library',
  'WaitlistRegistrations',
  'Licenses',
  'POECRenewalItems',
  'UserFaculty',
  'UpcomingRegistrations'
] as const;

const REDUCER_PATH = 'api/client';

const createBaseQueryPipeline = (query: ReturnType<typeof fetchBaseQuery>) =>
  baseQueryWithAuth(baseQueryWithRetry(baseQueryWithCircuitBreaker(baseUrl, query)));

const rawBaseQuery = fetchBaseQuery({
  baseUrl,
  prepareHeaders,
  responseHandler: responseHandlerNoAuthRedirect,
  credentials: 'include',
  mode: 'cors'
});

// Base query WITHOUT timeout - for large file downloads and long-running requests
const baseQueryWithoutTimeout = createBaseQueryPipeline(rawBaseQuery);

// Base query WITH timeout - for normal requests
const baseQueryWithFullFeatures = createBaseQueryPipeline(
  baseQueryWithTimeout(baseUrl, rawBaseQuery)
);

export const repeatAction = async (
  args: string | FetchArgs,
  endpoint: string,
  store: Store,
  extraOptions?: baseQueryExtraOptions
) => {
  const _args = args as FetchArgs;
  if (_args) {
    let promise: UnknownAction;
    if (_args.method === 'POST' && !extraOptions?.ignoreDelayedAction) {
      const { initiate } = (ClientAPI.endpoints as any)[endpoint] as unknown as ApiEndpointMutation<
        MutationDefinition<any, any, any, any, any>,
        any
      >;
      const param =
        (args as FetchArgs).body === undefined ? (args as any).param : (args as FetchArgs).body;
      promise = await store.dispatch(initiate(param, {}) as unknown as UnknownAction);
      if ('error' in promise) {
        throw new Error('Failed executing delayed action');
      }
    } else {
      /*  const {
          initiate
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } = ((ClientAPI.endpoints as any)[endpoint] as unknown as ApiEndpointQuery<QueryDefinition<any, any, any, any, any>, any>);
        promise = store.dispatch(initiate({}, {}) as unknown as UnknownAction);*/
      return;
    }
    return promise;
  } else {
    return;
  }
};

// Main ClientAPI with timeout (default for most requests)
export const ClientAPI = createApi({
  reducerPath: REDUCER_PATH,
  tagTypes: CLIENT_API_TAG_TYPES,
  baseQuery: baseQueryWithFullFeatures,
  endpoints: () => ({})
});

// Alternative ClientAPI without timeout - for long-running operations like file downloads
export const ClientAPIWithoutTimeout = createApi({
  reducerPath: REDUCER_PATH,
  tagTypes: CLIENT_API_TAG_TYPES,
  baseQuery: baseQueryWithoutTimeout,
  endpoints: () => ({})
});
